import React, { useState } from 'react';
import { useSummarizer, getSourceTypeFromFile } from '../../../../core/store/useSummarizer';
import { FileText, FileVideo, FileAudio, File, X, Upload } from 'lucide-react';
import { SummaryOptionsBar, ProcessingButton } from '@components/ui';

interface FileConfig {
  enabled: boolean;
  acceptedTypes?: string[];
  maxSize?: number;
  placeholder?: string;
  multiple?: boolean;
  helpText?: string;
  showHelpText?: boolean;
}

interface FileInputProps {
  config: FileConfig;
  disabled?: boolean;
  className?: string;
  primaryColor?: string;
  submitButtonText?: string;
  showSummaryOptions?: boolean;
  showSummaryType?: boolean;
  showTone?: boolean;
}

const FileInput: React.FC<FileInputProps> = ({
  config,
  disabled = false,
  className = '',
  primaryColor: _,
  submitButtonText = 'Generate Summary',
  showSummaryOptions = true,
  showSummaryType = true,
  showTone = true,
}) => {
  const {
    selectedFile,
    fileError,
    uploadProgress,
    uploadedUrl,
    isUploading,
    summaryType,
    tone,
    setSelectedFile,
    validateFile,
    clearError,
    setSummaryType,
    setTone,
    uploadFile,
    summarize,
    isLoading: storeLoading,
    setError,
  } = useSummarizer();

  const [dragActive, setDragActive] = useState(false);

  const handleSubmit = async () => {
    if (!selectedFile || !uploadedUrl) return;

    try {
      const sourceType = getSourceTypeFromFile(selectedFile.name);

      await summarize({
        link_or_id: uploadedUrl,
        source: sourceType,
        extension: selectedFile.name?.split('.').pop(),
        summaryType,
        tone,
      });
    } catch (error) {
      // Error is handled by the store
    }
  };

  const handleFileSelect = async (file: File) => {
    // Clear any existing errors first
    clearError();

    // Validate file first
    const { isValid, error } = validateFile(file, {
      acceptedTypes: config.acceptedTypes,
      maxSize: config.maxSize,
    });

    if (!isValid) {
      setError(error ?? 'File validation failed');
      return; // Don't proceed with invalid file
    }

    setSelectedFile(file);

    // Upload file automatically
    try {
      await uploadFile(file);
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
  };

  const formatFileSize = (bytes: number | string): string => {
    if (!bytes) return '0 Bytes';
    if (typeof bytes === 'string') {
      bytes = parseInt(bytes);
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName?.split('.').pop()?.toLowerCase() || '';
    if (['pdf', 'doc', 'docx', 'txt'].includes(extension)) {
      return <FileText className="w-8 h-8 text-blue-500" />;
    }
    if (['mp4', 'avi', 'mov', 'mkv', 'webm'].includes(extension)) {
      return <FileVideo className="w-8 h-8 text-purple-500" />;
    }
    if (['mp3', 'wav', 'm4a', 'aac', 'ogg'].includes(extension)) {
      return <FileAudio className="w-8 h-8 text-green-500" />;
    }
    return <File className="w-8 h-8 text-gray-500" />;
  };

  const canSubmit =
    selectedFile && uploadedUrl && !fileError && !storeLoading && !disabled && !isUploading;
  const loading = storeLoading;

  return (
    <div className={`file-input space-y-3 ${className}`}>
      {/* Enhanced File Display */}
      {selectedFile ? (
        <div className="space-y-4">
          <div className="selected-file p-6 border-2 border-gray-200 rounded-xl bg-gradient-to-br from-gray-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  {getFileIcon(selectedFile.name)}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 text-lg">{selectedFile.name}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    {formatFileSize(selectedFile.size)} • {selectedFile.type || 'Unknown type'}
                  </div>
                  {isUploading && (
                    <div className="mt-3">
                      <div className="relative w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500 ease-out"
                          style={{ width: `${uploadProgress}%` }}
                        >
                          <div className="absolute right-0 top-0 h-full w-8 bg-white/30 blur-xl animate-pulse" />
                        </div>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-gray-500">Uploading...</span>
                        <span className="text-xs font-medium text-blue-600">{uploadProgress}%</span>
                      </div>
                    </div>
                  )}
                  {uploadedUrl && !isUploading && (
                    <div className="mt-2 flex items-center gap-1 text-green-600 text-sm">
                      <div className="w-2 h-2 bg-green-600 rounded-full" />
                      <span>Ready to summarize</span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedFile(null);
                }}
                disabled={loading || disabled}
                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => {
            if (!disabled && !loading) {
              clearError(); // Clear any existing errors when user clicks to select new file
              document.getElementById('file-input')?.click();
            }
          }}
          className={`file-dropzone relative p-12 border-2 border-dashed rounded-xl text-center cursor-pointer transition-all duration-200 ${
            fileError
              ? 'border-red-400 bg-gradient-to-br from-red-50 to-red-50'
              : dragActive
                ? 'border-blue-400 bg-gradient-to-br from-blue-50 to-indigo-50 transform scale-[1.02]'
                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          } ${disabled || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <div className="flex flex-col items-center">
            <div
              className={`p-4 rounded-full mb-4 transition-transform duration-200 ${
                dragActive ? 'bg-blue-100 transform scale-110' : 'bg-gray-100'
              }`}
            >
              <Upload className={`w-8 h-8 ${dragActive ? 'text-blue-600' : 'text-gray-600'}`} />
            </div>
            <div className="text-lg font-medium text-gray-700 mb-2">
              {dragActive
                ? 'Drop your file here'
                : config.placeholder || 'Drag & drop or click to upload'}
            </div>
            <div className="text-sm text-gray-500 space-y-1">
              {config.acceptedTypes && <div>Supported: {config.acceptedTypes.join(', ')}</div>}
              {config.maxSize && <div>Max size: {formatFileSize(config.maxSize)}</div>}
            </div>
            {!dragActive && (
              <button className="mt-4 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors">
                Browse files
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error Display - Positioned immediately after dropzone/file display */}
      {fileError && (
        <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">File validation failed</p>
              <div className="mt-1 text-sm text-red-700">{fileError}</div>
              {(config.acceptedTypes || config.maxSize) && (
                <div className="mt-2 text-sm text-red-600">
                  <p className="font-medium">Requirements:</p>
                  <ul className="mt-1 list-disc list-inside space-y-1">
                    {config.acceptedTypes && (
                      <li>Supported formats: {config.acceptedTypes.join(', ')}</li>
                    )}
                    {config.maxSize && <li>Maximum file size: {formatFileSize(config.maxSize)}</li>}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hidden File Input */}
      <input
        id="file-input"
        type="file"
        accept={config.acceptedTypes?.join(',')}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileSelect(file);
        }}
        className="hidden"
        disabled={loading || disabled}
        multiple={config.multiple}
      />

      {config.helpText && config.showHelpText && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">{config.helpText}</p>
        </div>
      )}

      {/* Summary Options and Submit Button - Horizontal Layout */}
      {uploadedUrl && (
        <div className="flex flex-col gap-2 sm:!flex-row items-center justify-between">
          {/* Summary Options */}
          {showSummaryOptions && (
            <SummaryOptionsBar
              summaryType={summaryType}
              tone={tone}
              onSummaryTypeChange={setSummaryType}
              onToneChange={setTone}
              disabled={loading || disabled || isUploading}
              containerClassName="justify-between sm:justify-normal w-full"
              className="flex !flex-row gap-3"
              summaryTypeProps={{ triggerClassName: 'w-full max-w-[160px] bg-white' }}
              toneProps={{ triggerClassName: 'w-full max-w-[160px] bg-white' }}
              showSummaryType={showSummaryType}
              showTone={showTone}
            />
          )}

          {/* Enhanced Submit Button */}
          <ProcessingButton
            onClick={handleSubmit}
            disabled={!canSubmit}
            isLoading={loading}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
            submitButtonText={submitButtonText}
            className="sm:ml-auto w-full sm:w-auto sm:min-w-[180px]"
          />
        </div>
      )}
    </div>
  );
};

export default FileInput;
